/**
 * Company News & Press Coverage — runtime localization layer.
 *
 * The raw, bilingual data now lives in AUTO-GENERATED modules built from the
 * Markdown/JSON in content/ by scripts/build-content.ts:
 *   - content/news/<id>.<lang>.md   → src/data/news.generated.ts
 *   - content/press/coverage.json   → src/data/press.generated.ts
 *
 * This module collapses them to the active language for the UI (mirroring
 * @/data/blog) and re-exports the raw arrays for build-time consumers (the
 * route manifest needs ids). It is DOM-free so it is safe to import from the
 * Node/tsx SEO/sitemap generator as well as the browser bundle.
 */

import { localizedText } from '../utils/i18n'
import type { NewsCategory } from '../types/newsContent'
import { newsPosts } from './news.generated'
import { pressItems } from './press.generated'

// Raw bilingual arrays (for build-time consumers that only need ids).
export const companyNewsData = newsPosts
export const pressCoverageData = pressItems

// Runtime (localized) shapes consumed by the News pages.
export interface NewsArticle {
  id: string
  date: string
  category: NewsCategory
  title: string
  /** Shorter <title>-tag variant; falls back to `title` when unset. */
  seoTitle?: string
  summary: string
  image?: string
  /** Rendered HTML body (markdown → HTML at build time). */
  html: string
  cta?: { url: string; label: string; note?: string }
}

export interface PressArticle {
  id: string
  date: string
  outlet: string
  outletLogo?: string
  image?: string
  title: string
  url: string
}

/** All company-news articles in the active language, newest-first. */
export function getCompanyNews(): NewsArticle[] {
  return newsPosts.map((p) => ({
    id: p.id,
    date: p.date,
    category: p.category,
    title: localizedText(p.title),
    seoTitle: p.seoTitle ? localizedText(p.seoTitle) : undefined,
    summary: localizedText(p.summary),
    image: p.image,
    html: localizedText(p.html),
    cta: p.cta
      ? {
          url: p.cta.url,
          label: localizedText(p.cta.label),
          note: p.cta.note ? localizedText(p.cta.note) : undefined,
        }
      : undefined,
  }))
}

/** All press-coverage items in the active language. */
export function getPressCoverage(): PressArticle[] {
  return pressItems.map((p) => ({
    id: p.id,
    date: p.date,
    outlet: p.outlet,
    outletLogo: p.outletLogo,
    image: p.image,
    title: localizedText(p.title),
    // Outlets that ran the story in both languages get a per-language link;
    // for single-language coverage the generator filled both sides already.
    url: localizedText(p.url),
  }))
}
